'use client';

import { VariantProps, cva } from 'class-variance-authority';
import classNames from 'classnames';
import { ElementType, HTMLProps, ReactNode, forwardRef } from 'react';

import { cn } from '@/lib/css-utils';

import { BaseUIComponentProps, defaultBaseUIComponentProps } from './base';
import { Loader } from './loader';
import { Paragraph } from './paragraph';
import { useGlobalDefaults } from './provider';

export const buttonVariants = cva(
    'flex gap-2 items-center justify-center rounded-lg cursor-pointer disabled:cursor-not-allowed border group relative focus:outline-none',
    {
        variants: {
            variant: {
                primary: '',
                secondary: '',
                tertiary: '',
                cta: '',
                icon: '',
                text: 'border-0 border-transparent focus:outline-none rounded-none',
            },
            mode: {
                light: '',
                dark: '',
            },
            size: {
                sm: '',
                md: '',
                lg: '',
                icon: 'p-2 h-6 w-6 flex items-center justify-center',
            },
            isLoading: {
                true: 'cursor-wait disabled:cursor-wait',
                false: '',
            },
            toggleActive: {
                true: '!border-token-fill-logo-light',
                false: '',
            },
        },

        compoundVariants: [
            /**
             * DARK MODE
             */
            {
                variant: 'primary',
                mode: 'dark',
                className:
                    'bg-neutral-50 border-neutral-50 hover:bg-neutral-100 hover:border-neutral-100 focus:bg-neutral-50 focus:border-neutral-50 disabled:bg-neutral-400 disabled:border-neutral-400',
            },
            {
                variant: 'secondary',
                mode: 'dark',
                className:
                    'bg-neutral-600 border-neutral-600 hover:bg-neutral-500 hover:border-neutral-500 focus:bg-neutral-400 focus:border-neutral-400 disabled:bg-neutral-400 disabled:border-neutral-400',
            },
            {
                variant: 'tertiary',
                mode: 'dark',
                className:
                    'border-neutral-600 hover:border-token-stroke-button-focused focus:shadow-focus focus:border-darkBlue disabled:border-neutral-500',
            },
            {
                variant: 'cta',
                mode: 'dark',
                className:
                    'bg-nBrand-500 border-nBrand-500 hover:bg-nBrand-100 hover:border-nBrand-100 focus:bg-nBrand-100 focus:border-nBrand-100 disabled:bg-nBrand-300 disabled:border-nBrand-300',
            },
            {
                variant: 'icon',
                mode: 'dark',
                className:
                    'border-token-stroke-button-default hover:border-token-stroke-button-hover focus:border-token-stroke-button-focused disabled:border-token-stroke-button-disabled',
            },

            /**
             * LIGHT MODE
             */
            {
                variant: 'primary',
                mode: 'light',
                className:
                    'bg-neutral-900 border-neutral-900 hover:bg-neutral-800 hover:border-neutral-800 focus:bg-neutral-900 focus:border-neutral-900 disabled:bg-neutral-300 disabled:border-neutral-300',
            },
            {
                variant: 'secondary',
                mode: 'light',
                className:
                    'bg-neutral-800 border-neutral-800 hover:bg-neutral-700 hover:border-neutral-700 focus:bg-neutral-800 focus:border-neutral-800 disabled:bg-neutral-300 disabled:border-neutral-300',
            },
            {
                variant: 'tertiary',
                mode: 'light',
                className:
                    'bg-transparent border-neutral-300 hover:border-neutral-500 focus:border-neutral-700 disabled:border-neutral-200',
            },
            {
                variant: 'cta',
                mode: 'light',
                className:
                    'bg-nBrand-500 border-nBrand-500 hover:bg-nBrand-600 hover:border-nBrand-600 focus:bg-nBrand-500 focus:border-nBrand-500 disabled:bg-nBrand-300 disabled:border-nBrand-300',
            },
            {
                variant: 'icon',
                mode: 'light',
                className:
                    'border-neutral-300 hover:border-neutral-500 focus:border-neutral-700 disabled:border-neutral-200',
            },

            /**
             * TOGGLE ACTIVE
             */
            {
                variant: 'primary',
                toggleActive: true,
                mode: 'dark',
                className: 'bg-neutral-50 border-neutral-50',
            },
            {
                variant: 'primary',
                toggleActive: true,
                mode: 'light',
                className: 'bg-neutral-900 border-neutral-900',
            },
            {
                variant: 'cta',
                toggleActive: true,
                className: 'bg-token-fill-button-primary-pressed',
            },
            {
                variant: 'secondary',
                toggleActive: true,
                className: 'bg-token-fill-button-secondary-pressed',
            },
            {
                variant: 'tertiary',
                toggleActive: true,
                mode: 'dark',
                className: 'border-neutral-200 bg-token-fill-button-secondary-pressed',
            },
            {
                variant: 'tertiary',
                toggleActive: true,
                mode: 'light',
                className: 'border-neutral-700 bg-neutral-100',
            },
            {
                variant: 'text',
                toggleActive: true,
                className: 'text-token-text-light-focused',
            },

            /**
             * SIZES
             */
            {
                size: 'sm',
                variant: 'primary',
                className: 'px-4 py-1 h-[32px]',
            },
            {
                size: 'sm',
                variant: 'secondary',
                className: 'px-4 py-1 h-[32px]',
            },
            {
                size: 'sm',
                variant: 'tertiary',
                className: 'px-4 py-1 h-[32px]',
            },
            {
                size: 'sm',
                variant: 'cta',
                className: 'px-4 py-1 h-[32px]',
            },

            {
                size: 'md',
                variant: 'primary',
                className: 'px-4 py-2 h-10',
            },
            {
                size: 'md',
                variant: 'secondary',
                className: 'px-4 py-2 h-10',
            },
            {
                size: 'md',
                variant: 'tertiary',
                className: 'px-4 py-2 h-10',
            },
            {
                size: 'md',
                variant: 'cta',
                className: 'px-4 py-2 h-10',
            },

            {
                size: 'lg',
                variant: 'primary',
                className: 'px-4 py-2.5 h-12',
            },
            {
                size: 'lg',
                variant: 'secondary',
                className: 'px-4 py-2.5 h-12',
            },
            {
                size: 'lg',
                variant: 'tertiary',
                className: 'px-4 py-2.5 h-12',
            },
            {
                size: 'lg',
                variant: 'cta',
                className: 'px-4 py-2.5 h-12',
            },

            {
                variant: 'text',
                size: 'sm',
                className: 'px-0 py-0 h-[18px] text-sm',
            },
            {
                variant: 'text',
                size: 'md',
                className: 'px-0 py-0 h-[21px]',
            },
        ],

        defaultVariants: {
            variant: 'primary',
            mode: 'dark',
            size: 'md',
            isLoading: false,
            toggleActive: false,
        },
    }
);

export const buttonTextVariants = cva('leading-4.5', {
    variants: {
        variant: {
            primary: '',
            secondary: '',
            tertiary: '',
            cta: '',
            text: '',
            icon: '',
        },
        mode: {
            light: '',
            dark: '',
        },
        disabled: {
            true: '',
            false: '',
        },
        size: {
            sm: '',
            md: '',
            lg: '',
            icon: '',
        },
        isLoading: {
            true: 'opacity-0',
            false: '',
        },
        toggleActive: {
            true: '',
            false: '',
        },
    },
    compoundVariants: [
        /**
         * PRIMARY
         */
        {
            variant: 'primary',
            mode: 'dark',
            disabled: false,
            className: 'text-neutral-700',
        },
        {
            variant: 'primary',
            mode: 'light',
            disabled: false,
            className: 'text-neutral-50',
        },
        {
            variant: 'primary',
            disabled: true,
            className: 'text-neutral-500',
        },

        /**
         * SECONDARY
         */
        {
            variant: 'secondary',
            mode: 'dark',
            disabled: false,
            className: 'text-neutral-50',
        },
        {
            variant: 'secondary',
            mode: 'light',
            disabled: false,
            className: 'text-neutral-50',
        },
        {
            variant: 'secondary',
            disabled: true,
            className: 'text-neutral-500',
        },

        /**
         * TERTIARY
         */
        {
            variant: 'tertiary',
            mode: 'dark',
            disabled: false,
            className: 'text-neutral-50 group-hover:text-neutral-50 group-focus:text-neutral-50',
        },
        {
            variant: 'tertiary',
            mode: 'light',
            disabled: false,
            className: 'text-neutral-900 group-hover:text-neutral-900 group-focus:text-neutral-900',
        },
        {
            variant: 'tertiary',
            disabled: true,
            className: 'text-neutral-400',
        },

        /**
         * CTA
         */
        {
            variant: 'cta',
            mode: 'dark',
            disabled: false,
            className: 'text-neutral-700',
        },
        {
            variant: 'cta',
            mode: 'light',
            disabled: false,
            className: 'text-neutral-50',
        },
        {
            variant: 'cta',
            disabled: true,
            className: 'text-neutral-500',
        },

        /**
         * TEXT
         */
        {
            variant: 'text',
            mode: 'dark',
            disabled: false,
            className: 'text-neutral-200 group-hover:text-neutral-50 group-focus:text-neutral-100',
        },
        {
            variant: 'text',
            mode: 'light',
            disabled: false,
            className: 'text-neutral-900 group-hover:text-neutral-700 group-focus:text-neutral-700',
        },
        {
            variant: 'text',
            disabled: true,
            className: 'text-neutral-400',
        },

        /**
         * ICON
         */
        {
            variant: 'icon',
            mode: 'dark',
            disabled: false,
            className: 'text-neutral-50 group-hover:text-neutral-50 group-focus:text-neutral-50',
        },
        {
            variant: 'icon',
            mode: 'light',
            disabled: false,
            className: 'text-neutral-900 group-hover:text-neutral-900 group-focus:text-neutral-900',
        },
        {
            variant: 'icon',
            disabled: true,
            className: 'text-neutral-400',
        },

        /**
         * TYPE SCALE
         */
        {
            variant: 'primary',
            className: 'text-xs leading-4.5',
        },
        {
            variant: 'secondary',
            className: 'text-xs leading-4.5',
        },
        {
            variant: 'tertiary',
            className: 'text-xs leading-4.5',
        },
        {
            variant: 'cta',
            className: 'text-xs leading-4.5',
        },
        {
            variant: 'text',
            size: 'sm',
            className: 'text-xs leading-4.5 h-[18px]',
        },
        {
            variant: 'text',
            size: 'md',
            className: 'text-md',
        },

        {
            variant: 'text',
            toggleActive: true,
            className: 'text-token-text-light-focused',
        },
    ],
    defaultVariants: {
        mode: 'dark',
    },
});

export type ButtonVariant = NonNullable<VariantProps<typeof buttonVariants>['variant']>;
export type ButtonSize = NonNullable<VariantProps<typeof buttonVariants>['size']>;

export type ButtonProps = Omit<HTMLProps<HTMLButtonElement>, 'disabled' | 'type' | 'size' | 'prefix' | 'suffix'> & {
    disabled?: boolean;

    variant?: ButtonVariant;
    size?: ButtonSize;

    toggleActive?: boolean;

    type?: 'button' | 'submit' | 'reset';

    prefix?: React.ReactNode;
    suffix?: React.ReactNode;

    textClassName?: string;

    isLoading?: boolean;
} & BaseUIComponentProps;

const getLoaderColor = (variant: ButtonVariant | undefined, mode: 'light' | 'dark' | undefined) => {
    switch (variant) {
        case 'primary':
            return mode === 'light' ? 'light' : 'dark';

        case 'cta':
            return 'dark';

        case 'secondary':
            return 'light';

        case 'tertiary':
        case 'text':
        case 'icon':
            return mode === 'light' ? 'dark' : 'light';

        default:
            return 'dark';
    }
};

const getLoaderSize = (size: ButtonSize | undefined) => {
    switch (size) {
        case 'sm':
        case 'icon':
            return 'sm';

        case 'lg':
            return 'md';

        default:
        case 'md':
            return 'sm';
    }
};

export const Button = forwardRef<HTMLButtonElement, ButtonProps>(
    (
        {
            className,
            variant,
            size,
            isLoading = false,
            mode: baseMode,
            type,
            children,
            textClassName,
            prefix,
            suffix,
            toggleActive,
            ...props
        },
        ref
    ) => {
        const defaults = useGlobalDefaults(defaultBaseUIComponentProps);
        const mode = baseMode ?? defaults.mode;

        return (
            <button
                type={type ?? 'button'}
                className={cn(
                    buttonVariants({
                        variant,
                        size,
                        isLoading: !!isLoading,
                        className,
                        toggleActive: !!toggleActive,
                        mode,
                    }),
                    className
                )}
                ref={ref}
                disabled={isLoading || props.disabled} // Disable the button while loading
                {...props}
            >
                {prefix ? (
                    <span
                        className={buttonTextVariants({
                            mode,
                            variant,
                            disabled: props.disabled || isLoading,
                            className: 'flex items-center justify-center',
                            isLoading: !!isLoading,
                            toggleActive: !!toggleActive,
                        })}
                    >
                        {prefix}
                    </span>
                ) : null}

                <Paragraph
                    className={cn(
                        buttonTextVariants({
                            mode,
                            variant,
                            disabled: props.disabled || isLoading,
                            size,
                            isLoading: !!isLoading,
                            toggleActive: !!toggleActive,
                        }),
                        textClassName
                    )}
                    as="div"
                >
                    {children}
                </Paragraph>

                {suffix ? (
                    <span
                        className={buttonTextVariants({
                            mode,
                            variant,
                            disabled: props.disabled || isLoading,
                            className: 'flex items-center justify-center',
                            isLoading: !!isLoading,
                            toggleActive: !!toggleActive,
                        })}
                    >
                        {suffix}
                    </span>
                ) : null}

                {isLoading ? (
                    <div className="absolute inset-0 flex items-center justify-center">
                        <Loader mode={getLoaderColor(variant, mode)} size={getLoaderSize(size)} />
                    </div>
                ) : null}
            </button>
        );
    }
);
Button.displayName = 'Button';

type IconButtonProps = Omit<ButtonProps, 'variant' | 'size'> &
    (
        | {
              children: ReactNode;
          }
        | {
              icon: ElementType;
          }
    );

export const IconButton = forwardRef<HTMLButtonElement, IconButtonProps>(({ children, ...props }, ref) => {
    const { icon: Icon, ...rest } = 'icon' in props ? props : { icon: undefined, ...props };

    return (
        <Button
            {...rest}
            ref={ref}
            variant="icon"
            size="icon"
            textClassName={cn(classNames('flex items-center justify-center', props.textClassName))}
        >
            {Icon ? <Icon size={16} /> : children}
        </Button>
    );
});

IconButton.displayName = 'IconButton';
